import { type ReadonlyURLSearchParams } from 'next/navigation';

import {
  type BrandResponse,
  type BrandsBatchBySlugResponse,
} from '@/modules/brands/aliases';
import { getBrandSlug } from '@/modules/brands/utils';
import { brandRegex, categoryRegex } from '@/modules/browse/constants';
import { formatQueryParams } from '@/modules/search/helpers';
import { getFiltersFromPath } from '@/modules/search/helpers/getFiltersFromPath';
import { getPathFromFilters } from '@/modules/search/helpers/getPathFromFilters';
import { isSortBy } from '@/modules/search/types';
import { type SearchFilters } from '@/modules/search/useSearchFilters';

/**
 * Decodes a composite size ID: `${variantSetId}.${variantId}-${locale}`
 * e.g. "52.1-UK"
 */
export function decodeCompositeSizeId(compositeId: string) {
  const dashIndex = compositeId.lastIndexOf('-');
  const compositeVariant =
    dashIndex !== -1 ? compositeId.slice(0, dashIndex) : compositeId;
  const locale =
    dashIndex !== -1 ? compositeId.slice(dashIndex + 1) : undefined;
  const [variantSetId, variantId] = compositeVariant.split('.');

  return {
    variantId: Number(variantId),
    variantSetId: Number(variantSetId),
    locale: locale || undefined,
  };
}

/**
 * Normalises a composite size ID to the format sent to the search endpoint (strips locale).
 * `${variantSetId}.${variantId}-${locale}` → `${variantSetId}.${variantId}`
 */
export function normaliseCompositeSizeId(compositeId: string) {
  const { variantSetId, variantId } = decodeCompositeSizeId(compositeId);
  return `${variantSetId}.${variantId}`;
}

export function normaliseCompositeSizeIds(compositeIds?: string[]) {
  return (compositeIds || []).map(normaliseCompositeSizeId);
}

export function transformFiltersToActivityTrackerPayload(
  searchFilters: SearchFilters
) {
  const sizes = searchFilters.sizes || [];

  const sizeVariantSetIds: number[] = [];
  const sizeVariantIds: number[] = [];
  const sizeLocales: (string | undefined)[] = [];

  for (const compositeId of sizes) {
    const { variantSetId, variantId, locale } =
      decodeCompositeSizeId(compositeId);
    sizeVariantSetIds.push(variantSetId);
    sizeVariantIds.push(variantId);
    sizeLocales.push(locale);
  }

  /**
   * We are setting default values on unset fields to match the legacy website.
   * It is not known whether this is strictly necessary.
   */
  return {
    brandFilter: searchFilters.brands ?? [],
    categoryFilter: searchFilters.groups ? [searchFilters.groups] : [],
    subcategoryFilter: searchFilters.productTypes ?? [],
    onSaleFilter: searchFilters.isDiscounted || false,
    colourFilter: searchFilters.colours ?? [],
    conditionFilter: searchFilters.conditions ?? [],
    sizeFilterVariantSetId: sizeVariantSetIds,
    sizeFilterVariantId: sizeVariantIds,
    sizeFilterCountry: sizeLocales,
    sizeFilterCategory: [] as number[],
    maxPriceFilter: searchFilters.priceMax,
    minPriceFilter: searchFilters.priceMin,
    /* We don't have saved sizes toggle on web */
    saveSizesToggle: false,
    /* Free shipping filter no longer exists */
    freeShippingFilter: false,
  };
}

export function hasActiveFilters(searchFilters: SearchFilters) {
  const {
    isDiscounted,
    brands,
    groups,
    colours,
    conditions,
    priceMax,
    priceMin,
    sizes,
  } = searchFilters;

  const hasOnSaleFilter = isDiscounted;
  const hasBrandFilters = Boolean(brands?.length);
  const hasGroupsFilter = Boolean(groups);
  const hasColourFilter = Boolean(colours?.length);
  const hasConditionFilter = Boolean(conditions?.length);
  const hasPriceFilter = Boolean(priceMin || priceMax);
  const hasSizeFilter = Boolean(sizes?.length);

  return (
    hasOnSaleFilter ||
    hasBrandFilters ||
    hasGroupsFilter ||
    hasColourFilter ||
    hasConditionFilter ||
    hasPriceFilter ||
    hasSizeFilter
  );
}

export function parseFilterParams(params: URLSearchParams): SearchFilters {
  const sort = params.get('sort');

  return {
    /** when migration is complete, we should rename the categories query param to be singular */
    brands: params.get('brands')?.split(','),
    isDiscounted: params.get('isDiscounted')
      ? params.get('isDiscounted') === 'true'
      : undefined,
    priceMin: Number(params.get('priceMin')) || undefined,
    priceMax: Number(params.get('priceMax')) || undefined,
    sizes: params.get('sizes')?.split(','),
    colours: params.get('colours')?.split(','),
    conditions: params.get('conditions')?.split(','),
    sort: isSortBy(sort) ? sort : undefined,
    discountTypes: params.get('discount_types') || undefined,
    minDiscount: params.get('min_discount') || undefined,
    maxDiscount: params.get('max_discount') || undefined,
    shippingId: params.get('shipping_id') || undefined,
    groups: params.get('groups') || undefined,
    productTypes: params.get('productTypes')?.split(',') || undefined,
    isKids: params.get('isKids') ? params.get('isKids') === 'true' : undefined,
    gender: params.get('gender') || undefined,
  };
}

/**
 * @PB TODO modify this function for use in the search/brand/category page files as well
 * to avoid duplicating the logic
 */
export function parseFiltersFromUrl({
  params,
  pathname,
  isPathParamsMode,
  brandData = {},
}: {
  params: ReadonlyURLSearchParams | URLSearchParams;
  pathname: string;
  isPathParamsMode: boolean;
  brandData?: BrandsBatchBySlugResponse;
}): SearchFilters {
  const searchFiltersFromParams = parseFilterParams(params);

  if (!isPathParamsMode) {
    return searchFiltersFromParams;
  }

  const [_, brandSlug] = pathname.match(brandRegex) ?? [];
  const [__, categoryPath] = pathname.match(categoryRegex) ?? [];

  const searchFiltersFromPath = getFiltersFromPath(categoryPath);

  const brand = Object.values(brandData).find(
    (brand) => brand.slug === brandSlug
  );
  const brandId = brand?.id ? String(brand.id) : undefined;

  return {
    ...searchFiltersFromParams,
    ...searchFiltersFromPath,
    ...(brandId && { brands: [brandId] }),
  };
}

export function buildPathParamsUrl({
  queryParams,
  brands,
  groups,
  productTypes,
  gender,
  isKids,
  brandsWithIds,
}: {
  queryParams: URLSearchParams;
  brands?: string[];
  groups?: string;
  productTypes?: string[];
  gender?: string;
  isKids?: boolean;
  brandsWithIds?: BrandResponse;
}) {
  /**
   * If there are multiple brands, multiple subcategories, or none at all
   * use /explore/ pathname with filters as query params
   *
   * e.g. https://www.depop.com/explore/?groups=bottoms&productTypes=skirts&gender=womens&brands=151%2C270
   *
   * Otherwise, construct a URL with:
   * - /brands/{brandSlug} if a brand is present
   * - /category/{categoryPath} if a category is present
   * - /c/ instead of /category/ if there are too many URL segments
   *
   * e.g. https://www.depop.com/ie/brands/nike/c/mens/tops/t-shirts/
   */
  const categoryPath = getPathFromFilters({
    groups,
    productTypes,
    gender,
    isKids,
  });
  const hasMultipleProductTypes = productTypes && productTypes.length > 1;

  const brandSlug = getBrandSlug(brandsWithIds, brands);
  const hasMultipleBrands = brands && brands.length > 1;

  if (!hasMultipleBrands && !hasMultipleProductTypes) {
    if (categoryPath && brandSlug) {
      const path = `/brands/${brandSlug}/c/${categoryPath}`;

      queryParams.delete('brands');
      queryParams.delete('groups');
      queryParams.delete('productTypes');
      queryParams.delete('gender');

      return `${path}/${formatQueryParams(queryParams)}`;
    } else if (brandSlug) {
      queryParams.delete('brands');

      return `/brands/${brandSlug}/${formatQueryParams(queryParams)}`;
    } else if (categoryPath) {
      queryParams.delete('groups');
      queryParams.delete('productTypes');
      queryParams.delete('gender');

      return `/category/${categoryPath}/${formatQueryParams(queryParams)}`;
    }
  }

  return `/explore/${
    queryParams.toString().length > 0 ? `?${queryParams.toString()}` : ''
  }`;
}
